import {Injectable} from '@angular/core';
import {
  AppConfigService,
  CustomerContact,
  CustomerContactService,
  CustomerService,
  FrontendAppConfigKey,
  FrontendAppSwitch,
  KeyValueStore,
  Ref,
  Role,
  UserAuthService,
} from '@lifeislife/lifeislife-domain';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {catchError, debounceTime, filter, map, publishReplay, refCount, switchMap, take} from 'rxjs/operators';
import {CustomerContextService} from './customer-context-service';

@Injectable()
export class CustomerContextServiceBrowser implements CustomerContextService {

  availableCustomerContacts$: Observable<Ref<CustomerContact>[]>;
  activeCustomerContact$ = new BehaviorSubject<CustomerContact | null>(null);
  hasActiveCustomerContact$: Observable<boolean>;

  constructor(
    private userAuthService: UserAuthService,
    private customerContactService: CustomerContactService,
    private customerService: CustomerService,
    private keyValueStore: KeyValueStore,
    private configService: AppConfigService,
  ) {

    this.availableCustomerContacts$ = this.userAuthService.state$.pipe(
      map(s => s == null ? [] : s.customerContactRefs),
      publishReplay(1), refCount(),
    );

    const singleContactSubscription = this.availableCustomerContacts$
      .subscribe(availableRefs => {
        if (availableRefs.length === 0) {
          this.setCustomerContactContext(null);
        } else if (availableRefs.length === 1) {
          this.setCustomerContactContext(availableRefs[0]);
        } else {
          this.activateStoredCustomerContact(availableRefs);
        }
      });
    // App-scoped service no need to unsubscribe

    this.hasActiveCustomerContact$ = this.activeCustomerContact$.pipe(
      map(c => c != null),
      publishReplay(1), refCount(),
    );
  }

  selectCustomerContactRef(ref: Ref<CustomerContact>) {
    this.setCustomerContactContext(ref);
  }

  getActiveCustomerRef() {
    const activeCustomerContact = this.activeCustomerContact$.getValue();
    if (activeCustomerContact == null) {
      return null;
    }
    return activeCustomerContact.customerRef;
  }

  getActiveContactRef() {
    const activeCustomerContact = this.activeCustomerContact$.getValue();
    if (activeCustomerContact == null) {
      return null;
    }
    return activeCustomerContact.contactRef;
  }

  activateCustomerContactContext(customerContactIdParam: string): void {
    if (customerContactIdParam == null) {
      return;
    }
    const customerContactId = parseInt(customerContactIdParam, 10);
    if (isNaN(customerContactId)) {
      return;
    }

    this.userAuthService.state$.pipe(
      debounceTime(1000), // Ensure auth is settled
      filter(s => s.status !== 'authenticating'),
      switchMap(() => this.availableCustomerContacts$),
      take(1),
    ).subscribe(ccList => this.activateCustomerContactId(customerContactId, ccList));

  }

  activateCustomerContext(customerIdParam: string): void {
    if (customerIdParam == null) {
      return;
    }
    const customerId = parseInt(customerIdParam, 10);
    if (isNaN(customerId)) {
      return;
    }

    const curCustomercontact = this.activeCustomerContact$.getValue();
    if (curCustomercontact && curCustomercontact.customerRef.id === customerId) {
      return;
    }

    this.userAuthService.state$.pipe(
      debounceTime(1000), // Ensure auth is settled
      filter(s => s.status !== 'authenticating'),
      switchMap(() => this.availableCustomerContacts$),
      take(1),
      switchMap(refList => {
        const cc$List = refList.map(ref => this.customerContactService.getCustomerContact$(ref));
        return cc$List.length === 0 ? of([]) : forkJoin(cc$List);
      }),
    ).subscribe(ccList => this.activateCustomerId(customerId, ccList));
  }


  private activateCustomerContactId(customerContactId: number, ccList: Ref<CustomerContact>[]) {
    const matchingCustomerContactRef = ccList.find(cc => cc.id === customerContactId);
    if (matchingCustomerContactRef) {
      this.selectCustomerContactRef(matchingCustomerContactRef);
    } else {
      this.customerContactService.getCustomerContact$({id: customerContactId}).pipe(
        switchMap(cc => this.customerService.getCustomerLabel$(cc.customerRef)),
        catchError(e => of(`(error while getting label: ${e.message})`)),
      ).subscribe(customerName => {
        const debug = this.configService.isSwitchCurrentlyEnabled(FrontendAppSwitch.front_dev_debug);
        if (debug) {
          console.log(`Deauthenticating: Want customer contact ${customerContactId}, which is unavailable`);
        }
        this.userAuthService.deauthenticate(`Veuillez vous identifier pour ${customerName}`);
      });
    }
  }

  private activateCustomerId(customerId: number, customerContactList: CustomerContact[]) {
    const matchingCustomerContact = customerContactList.find(cc => cc.customerRef.id === customerId);
    if (matchingCustomerContact) {
      this.selectCustomerContactRef(matchingCustomerContact);
    } else {
      const debug = this.configService.isSwitchCurrentlyEnabled(FrontendAppSwitch.front_dev_debug);
      if (debug) {
        console.log(`Deauthenticating: Want customer customer ${customerId}, which is unavailable`);
      }
      // We cannot get customer name as we might not be logged in
      this.userAuthService.deauthenticate();
    }
  }

  private getCustomerContactStorageKey() {
    const configKey = this.configService.getCurrentConfigValue(FrontendAppConfigKey.app_configKey);
    return `${configKey}.customerContact`;
  }

  private getStoredCustomerContactId(): number | null {
    const storageKey = this.getCustomerContactStorageKey();
    const storedString = this.keyValueStore.getValue(storageKey);
    if (storedString == null) {
      return null;
    }
    const storedInt = parseInt(storedString, 10);
    if (isNaN(storedInt)) {
      return null;
    }
    return storedInt;
  }

  private storeCustomerContactId(c: CustomerContact) {
    const storageKey = this.getCustomerContactStorageKey();
    if (c == null) {
      this.keyValueStore.clearValue(storageKey);
    } else {
      this.keyValueStore.putValue(storageKey, `${c.id}`);
    }
  }

  private activateStoredCustomerContact(availableRefs: Ref<CustomerContact>[]) {
    const storedId = this.getStoredCustomerContactId();
    const availableRefIndex = availableRefs.findIndex(ref => ref.id === storedId);
    if (availableRefIndex >= 0) {
      const ref = availableRefs[availableRefIndex];
      this.setCustomerContactContext(ref);
    } else {
      this.setCustomerContactContext(null);
    }
  }

  private setCustomerContactContext(ref: Ref<CustomerContact>) {
    if (ref == null) {
      this.activeCustomerContact$.next(null);
      this.storeCustomerContactId(null);
      this.configService.setContactTypeContext(Role.CUSTOMER, null, null);
      this.configService.setTrusteeContext(null);
    } else {
      this.customerContactService.getCustomerContact$(ref)
        .subscribe(customerContact => {
          this.activeCustomerContact$.next(customerContact);
          this.storeCustomerContactId(customerContact);
          this.configService.setContactTypeContext(Role.CUSTOMER, null, customerContact);

          if (customerContact == null) {
            this.configService.setTrusteeContext(null);
          } else {
            this.customerService.getCustomer$(customerContact.customerRef)
              .subscribe(c => this.configService.setTrusteeContext(c.trusteeRef));
          }
        });
    }
  }

}
